import authService from "components/Auth/api-authorization/AuthorizeService";
import { clearLocalStoragePreservingSamlLoginOption } from "components/Auth/samlLoginOptionCache";
import { ITab } from "components/Navigation/NavMenuNew";
import type { ImpersonationState } from "models/ImpersonationState";
import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from "mobx";
import { User, UserManager } from "oidc-client-ts";
import ImpersonationService from "services/ImpersonationService";
import { Auth } from "utils/AccessTokens";
import { delay } from "utils/helpers";
import {
    AccessibleTenantOption,
    buildAccessibleTenantOptions,
    resolveRequestOrganizationId,
} from "utils/tenantAccess";
import initializePendo from "utils/Pendo";
import HomeDashboardStore from "./HomeDashboardStore";
import HomePageStore from "./HomePageStore";
import type { OrganizationStore } from "./OrganizationStore";
import type PermissionStore from "./PermissionStore";
import { IRootStore } from "./RootStore";
import OrgOptionsStore from "./OrgOptionsStore";

export enum AuthStatus {
    NotInitialized,
    Initializing,
    Initialized,
}

export class AuthStore {
    @observable authExpiring;
    @observable userManager: UserManager;
    @observable _user: User;
    @observable _isAuthenticated: boolean = false;

    @observable.ref permStore: PermissionStore;
    @observable.ref orgStore: OrganizationStore;
    @observable.ref orgOptionsStore: OrgOptionsStore;
    @observable.ref homePageStore: HomePageStore;
    @observable.ref homeDashboardStore: HomeDashboardStore;

    @observable private _expired: boolean = false;
    @observable status: AuthStatus = AuthStatus.NotInitialized;
    @observable currentState:
        | "default"
        | "constructing"
        | "ready"
        | "loading auth"
        | "auth loaded"
        | "initializing auth dependencies"
        | "dependencies loaded"
        | "default" = "default";

    @observable activeOrgId?: string = undefined;
    @observable.ref impersonationState?: ImpersonationState = undefined;

    @observable _roles: Map<string, string> = new Map();

    // ALlows us to use the normal auth flow for connecting 3rd partys apps
    @observable connectionAuthorizationUri: string;

    // private userService: UserService;
    _popUpDisabled = true;
    ApplicationName: string = "acxplatform";
    prefix = "/Authenticate";
    ApplicationPaths = {
        DefaultLoginRedirectPath: "/",
        ApiAuthorizationClientConfigurationUrl: `/_configuration/acxplatform`,
        ApiAuthorizationPrefix: this.prefix,
        Login: `${this.prefix}/Login`,
        // LoginFailed: `${prefix}/${LoginActions.LoginFailed}`,
        // LoginCallback: `${prefix}/${LoginActions.LoginCallback}`,
        // Register: `${prefix}/${LoginActions.Register}`,
        // Profile: `${prefix}/${LoginActions.Profile}`,
        // LogOut: `${prefix}/${LogoutActions.Logout}`,
        // LoggedOut: `${prefix}/${LogoutActions.LoggedOut}`,
        LoggedOut: "/",
        // LogOutCallback: `${prefix}/${LogoutActions.LogoutCallback}`,
        // IdentityRegisterPath: '/Identity/Account/Register',
        // IdentityManagePath: '/Identity/Account/Manage'
    };
    QueryParameterNames = {
        ReturnUrl: "returnUrl",
        Message: "message",
    };
    private signinRetries = 2;
    private readonly impersonationService = new ImpersonationService();
    private readonly oidcUserStorageKeyPrefix = `${this.ApplicationName}user:`;

    private readonly handleExternalLogout = (event: StorageEvent) => {
        if (
            !event.key?.startsWith(this.oidcUserStorageKeyPrefix) ||
            event.newValue !== null
        ) {
            return;
        }

        authService.updateState(null);
        runInAction(() => {
            this._user = undefined!;
            this._isAuthenticated = false;
            this._expired = false;
            this.impersonationState = undefined;
        });

        window.location.replace(
            `/authentication/login?returnUrl=${encodeURIComponent(
                window.location.href,
            )}`,
        );
    };

    constructor(public rootStore: IRootStore) {
        makeObservable(this);
        window.addEventListener("storage", this.handleExternalLogout);
        reaction(
            () => ({
                appInsights:
                    this.orgStore?.selectedOrganization?.appConfiguration
                        ?.appInsights,
            }),
            async ({ appInsights }) => {
                if (appInsights) {
                    this.rootStore.appInsightsProvider.initializeApplicationInsights(
                        appInsights,
                    );
                }

                const permissionServiceResponse =
                    await this.permStore.permissionService.getAppPermission(
                        this.effectiveUserId,
                    );

                const user = permissionServiceResponse.user;
                //Org name:
                this.rootStore.appInsightsProvider.appInsightsInstance?.addTelemetryInitializer(
                    (envelope) => {
                        if (envelope.tags) {
                            envelope.tags["ai.user.accountId"] =
                                user.organizationId;
                            envelope.tags["ai.user.tenantName"] =
                                this.orgStore.selectedOrganization?.name;
                            envelope.tags["ai.user.roleName"] =
                                this.permStore.user.role.name;
                            envelope.tags["ai.user.userName"] =
                                this.permStore.user.userName;
                        }

                        envelope.baseData = envelope.baseData || {};
                        envelope.baseData.properties =
                            envelope.baseData.properties === undefined
                                ? {}
                                : envelope.baseData.properties;
                        envelope.baseData.properties["ai.user.accountId"] =
                            user.organizationId;
                        envelope.baseData.properties["ai.user.roleName"] =
                            this.permStore.user.role.name;
                        envelope.baseData.properties["ai.user.userName"] =
                            this.permStore.user.userName;
                        envelope.baseData.properties["ai.user.tenantName"] =
                            this.orgStore.selectedOrganization?.name;
                        envelope.baseData.properties["ai.user.Nav"] =
                            window.location.pathname;
                        envelope.baseData.properties["ai.user.tenantName"] =
                            this.orgStore.selectedOrganization?.name;
                    },
                );
                this.rootStore.appInsightsProvider?.appInsightsInstance?.trackPageView(
                    {
                        properties: {
                            user_tenantName:
                                this.orgStore.selectedOrganization?.name,
                            user_roleName: this.permStore.user.role.name,
                            user_userName: this.permStore.user.userName,
                        },
                    },
                );
            },
        );

        runInAction(() => (this.currentState = "constructing"));

        this.populateAuth();
    }

    @action
    setCurrentPage(currentPage: string) {
        this.rootStore.setActiveLocation(currentPage);
    }

    async populateAuth(): Promise<Auth | undefined> {
        runInAction(() => {
            this.currentState = "loading auth";
        });

        await this.ensureUserManagerInitialized();
        const [isAuthenticated, user] = await Promise.all([
            authService.isAuthenticated(),
            authService.getUserObject(),
        ]);

        runInAction(() => {
            this._user = user!;
            this._isAuthenticated = isAuthenticated;
            this.status =
                this.status === AuthStatus.NotInitialized
                    ? AuthStatus.Initializing
                    : this.status;
            this.currentState = "auth loaded";
        });

        if (!this.areDependenciesInitialized() && isAuthenticated) {
            await this.initializeDependencies();
        }

        if (user) {
            await this.refreshImpersonationState();
            return { isAuthenticated, user };
        }

        runInAction(() => {
            this.impersonationState = undefined;
        });
    }

    private async initializeDependencies() {
        runInAction(() => {
            this.currentState = "initializing auth dependencies";
        });

        try {
            await this.refreshImpersonationState();
            const [
                orgStore,
                permStore,
                homePageStore,
                homeDashboardStore,
                orgOptionsStore,
            ] = await Promise.all([
                import("./OrganizationStore"),
                import("./PermissionStore"),
                import("./HomePageStore"),
                import("./HomeDashboardStore"),
                import("./OrgOptionsStore"),
            ]);

            const permission_store = new permStore.default();
            const org_store = new orgStore.OrganizationStore(
                this.rootStore,
                this.effectiveOrgId,
                this.setActiveOrgId,
            );

            // Assign the stores before loading so getRequestHeader can read the
            // impersonated user's tenant authorizations and org selection while
            // the remaining dependencies load. Until authorizations load, the
            // allowed set is just the home org, so the header safely stays on
            // the home tenant during this bootstrap window.
            runInAction(() => {
                this.permStore = permission_store;
                this.orgStore = org_store;
            });

            // Load the effective (impersonated) user's tenant authorizations
            // first, so the persisted org selection can be validated against
            // them and permissions load under the correct tenant (AUT-10307).
            await permission_store.loadTenantAuthorizations(
                this.effectiveUserId,
            );

            await org_store.loadOrganizations();

            // While impersonating, restrict the initial selection to the server
            // boundary (intersection of the acting and impersonated users' access);
            // undefined means "not impersonating" -> no restriction. (AUT-10623)
            const allowedOrgIds = this.impersonationAllowedOrgIds;

            await org_store.initSelectedOrganization(allowedOrgIds);

            // Load permissions under the validated selected tenant (not just the
            // home org), so a multi-tenant impersonated user gets the right
            // per-tenant permission context. (AUT-10307)
            await permission_store.loadPerms(
                this.effectiveUserId,
                org_store.selectedOrganization?.id ?? this.effectiveOrgId,
            );

            initializePendo(
                permission_store.user,
                org_store.selectedOrganization,
            );

            const org_options_store = new orgOptionsStore.default(
                this.rootStore,
            );

            runInAction(() => {
                this.orgOptionsStore = org_options_store;
            });
            const home_page_store = new homePageStore.default(this.rootStore);
            const home_dashboard_store = new homeDashboardStore.default(
                this.rootStore,
            );

            runInAction(() => {
                this.homePageStore = home_page_store;
                this.homeDashboardStore = home_dashboard_store;
                this.currentState = "dependencies loaded";
            });

            if (this.canUserView("Signals")) {
                await this.homePageStore.initHomePageValues();
                await this.homeDashboardStore.initHomeDashboardValues();
            }
            runInAction(() => {
                this.status = AuthStatus.Initialized;
                this.currentState = "ready";
            });

            this.setupDocumentVisibilityChange();
        } catch (e) {
            console.error(e);
            this.handleAuthorizationFailure();
        }
    }

    setActiveOrgId = (orgId?: string) => {
        if (orgId !== this.activeOrgId) {
            this.activeOrgId = orgId;
        }
    };

    getRequestHeader(useTemp: boolean = false) {
        const token: string | null = this._user?.access_token;

        // Tenant precedence (unchanged): an explicit temporary selection, then
        // the active org, then the globally selected org.
        const requestedOrgId =
            (useTemp ? this.orgStore?.temporarySelectOrganization?.id : null) ??
            this.activeOrgId ??
            this.orgStore?.selectedOrganization?.id;

        // While impersonating, constrain the header to the server-computed boundary
        // (intersection of the acting and impersonated users' access). Anything else
        // falls back to the impersonated user's home org — which lets a multi-tenant
        // user switch tenants (AUT-10307) while preventing a persisted admin org from
        // leaking into the header and locking the session out (AUT-9660 guard). If the
        // boundary is not yet loaded, the home-org fallback applies; the backend
        // validates every request regardless. (AUT-10623)
        const orgId = resolveRequestOrganizationId({
            isImpersonating: this.isImpersonating,
            requestedOrgId,
            effectiveOrgId: this.effectiveOrgId,
            allowedOrgIds: this.impersonationAllowedOrgIds,
        });

        let head: any;
        if (token) {
            head = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                "Acx-OrganizationId": orgId!,
                "Acx-Timezone":
                    Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
        } else {
            head = { "Content-Type": "application/json" };
        }
        return head;
    }

    @computed
    get isExpired() {
        return this._expired;
    }

    createArguments(state?: any) {
        return { useReplaceToNavigate: true, data: state };
    }

    success(state) {
        return { status: "success", state };
    }

    error(message) {
        return { status: "fail", message };
    }

    redirect() {
        return { status: "redirect" };
    }

    async getUserObject() {
        if (this._user) {
            return this._user;
        }
        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();
        if (user) {
            runInAction(() => {
                this._user = user;
            });
        }
        return user;
    }

    async getAccessToken() {
        if (this._user) {
            return this._user.access_token;
        }
        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();
        if (user) {
            runInAction(() => {
                this._user = user;
            });
        }
        return user && user.access_token;
    }

    async clearUser() {
        await this.ensureUserManagerInitialized();
        this.userManager.removeUser();
        this.userManager.clearStaleState();
        runInAction(() => {
            this.impersonationState = undefined;
        });
    }

    private expiredCallback = async () => {
        runInAction(() => {
            this._expired = true;
        });
    };
    private userSignedOutCallback = async () => {
        await this.userManager.removeUser();
        window.location.assign("/sessionexpired");
    };

    private userLoadedCallback = async () => {
        const req = await authService.getUserObject();
        if (req) {
            this._user = req;
            await this.refreshImpersonationState();
        }
    };

    private silentRenewErrCallback = async (error) => {
        await this.silentRenewAccess();
    };

    async ensureUserManagerInitialized() {
        if (!authService.userManager) {
            await authService.ensureUserManagerInitialized();
        }
        if (!this.userManager) {
            if (authService.userManager) {
                this.userManager = authService.userManager;
            }
        }

        if (this.userManager) {
            this.userManager.events.removeAccessTokenExpired(
                this.expiredCallback,
            );
            this.userManager.events.removeUserSignedOut(
                this.userSignedOutCallback,
            );
            this.userManager.events.removeUserLoaded(this.userLoadedCallback);
            this.userManager.events.removeSilentRenewError(
                this.silentRenewErrCallback,
            );

            this.userManager.events.addUserSignedOut(
                this.userSignedOutCallback,
            );

            this.userManager.events.addAccessTokenExpired(this.expiredCallback);

            this.userManager.events.addSilentRenewError(
                this.silentRenewErrCallback,
            );

            this.userManager.events.addUserLoaded(this.userLoadedCallback);
        }
    }

    getLoginUrl() {
        const redirectUrl = `${this.ApplicationPaths.Login}?${
            this.QueryParameterNames.ReturnUrl
        }=${encodeURIComponent(window.location.href)}`;
        return redirectUrl;
    }

    getReturnUrl(state) {
        const params = new URLSearchParams(window.location.search);
        const fromQuery = params.get(this.QueryParameterNames.ReturnUrl);
        if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
            // This is an extra check to prevent open redirects.
            throw new Error(
                "Invalid return url. The return url needs to have the same origin as the current page.",
            );
        }
        return (
            (state && state.returnUrl) ||
            fromQuery ||
            `${window.location.origin}${this.ApplicationPaths.LoggedOut}`
        );
    }

    @action
    handleAuthorizationFailure = async () => {
        try {
            await this.userManager.clearStaleState().finally(async () => {
                await this.silentRenewAccess();
            });
        } catch (e) {
            console.error(`AuthStore::handleAuthorizationFailure - ${e}`);

            this.redirectToSignIn();
        }
    };

    private areDependenciesInitialized(): boolean {
        // The stores are now assigned early (so getRequestHeader can read tenant
        // authorizations during bootstrap), so store presence alone no longer
        // means initialization succeeded. Require the terminal state and every
        // store so a failed bootstrap can retry and replace partial stores.
        return (
            this.status === AuthStatus.Initialized &&
            Boolean(this.permStore) &&
            Boolean(this.orgStore) &&
            Boolean(this.orgOptionsStore) &&
            Boolean(this.homePageStore) &&
            Boolean(this.homeDashboardStore)
        );
    }

    private redirectToSignIn() {
        const origin = window.location.href;
        window.location.href = `/authentication/login?returnUrl=${encodeURIComponent(
            origin,
        )}`;
    }

    @action
    setupDocumentVisibilityChange = () => {
        document.addEventListener("visibilitychange", async () => {
            // we only care when the user switches to the page.
            if (document.hidden === false) {
                // Getting the user will re-initialize all access-token events and the
                // associated timers to implement expiry checking/silent renewal
                const user = await this.userManager.getUser();

                if (user && user.expired) {
                    // If the token has already expired, there won't be any new events
                    // coming from oidc-client, so we need to handle this manually.
                    // dispatch(userExpired());
                    let retryStep = 0;
                    while (retryStep < this.signinRetries) {
                        retryStep += 1;
                        try {
                            // return await this.userManager.signinSilent();
                            return await this.silentRenewAccess();
                        } catch (err) {
                            // This may fail while the OS reconnects to a network
                            // after waking up and we were a bit too quick trying
                            // to perform the signin. We will retry a few times.
                        }
                    }
                }
            }
        });
    };

    @action
    silentRenewAccess = async () => {
        await this.ensureUserManagerInitialized();

        try {
            const user = await this.userManager.signinSilent();
            if (!user) {
                throw new Error("Silent renewal completed without a user.");
            }
            runInAction(() => {
                this._user = user;
                this._expired = false;
            });
            await this.refreshImpersonationState();
            return user;
        } catch (err) {
            return undefined;
        }
    };

    @action
    handleUserLogout = async (redirect?: boolean) => {
        const headers = this.getRequestHeader();

        const logoutURL = `/api/Authenticate/Logout`;

        try {
            const response = await fetch(logoutURL, {
                method: "Post",
                headers: headers,
                body: JSON.stringify({ ReturnUrl: "/" }),
            });

            if (response.status === 200) {
                await authService.userManager?.removeUser();
                sessionStorage.removeItem("backupConversationFilters");
                clearLocalStoragePreservingSamlLoginOption();
                await delay(200);

                if (redirect) {
                    window.location.assign("/sessionexpired");
                } else {
                    window.location.replace(
                        `/authentication/login?returnUrl=${encodeURI(
                            origin,
                        )}/app`,
                    );
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    public isAuthcxUser(): boolean {
        if (this.permStore.user?.organization?.name === "Authcx") {
            return true;
        }
        return false;
    }

    public isUserUltra(): boolean {
        if (
            this.permStore.user?.organization?.name === "Authcx" &&
            this.permStore.user.role?.name === "Ultra"
        ) {
            return true;
        }
        return false;
    }

    //Universal check for whether a user can edit V2 roles.
    public canUserEditRoles(): boolean {
        if (this.isUserUltra()) {
            return true;
        }
        return this.permStore.user.role.isOrgAdministrator;
    }

    private canUserPerformAction(action: string, resource: string): boolean {
        //If the organization doesn't have enhanced permissions enabled, then for backwards compatibility purposes the user has permission
        //Note: this is valid because other permission check for non-V2 roles and permissions still exist in the code
        if (!this.orgStore.selectedOrganization?.enhancedPermissionStatus) {
            return true;
        }

        //If the user is ultra, they get all permissions except resources in the explicit-check list.
        if (this.isUserUltra()) {
            return true;
        }

        //Ask the perm store about this action and resource, only the perm store knows what permissions a user's role has.
        return this.permStore.userHasPermissionTo(action, resource);
    }

    //TODO: define resource for function help
    //V2 Check for view permissions, documentation applies to all actions
    public canUserView(resource: string): boolean {
        return this.canUserPerformAction("View", resource);
    }

    //V2 Check for edit permissions
    public canUserEdit(resource: string): boolean {
        return this.canUserPerformAction("Edit", resource);
    }

    //V2 Check for manage permissions
    public canUserManage(resource: string): boolean {
        return this.canUserPerformAction("Manage", resource);
    }

    //V2 Check for multiple permissions on an OR logic basis for edit permissions
    public canUserEditAny(resources: string[]) {
        for (const resource of resources) {
            if (this.canUserEdit(resource)) {
                return true;
            }
        }
        return false;
    }

    public isLoggedInUserAgent(): boolean {
        return !!this.permStore.agent;
    }

    public filterTabs(tabs: ITab[]): ITab[] {
        return this.permStore.filterTabs(tabs);
    }

    public canUserViewAdminApp(): boolean {
        return this.canUserView("Administrative App");
    }

    @computed
    get isImpersonating(): boolean {
        return this.impersonationState?.isImpersonating === true;
    }

    // The server-computed boundary for the impersonation session: the intersection
    // of the acting and impersonated users' organizations. Used to constrain tenant
    // selection and the request header. undefined when not impersonating (no
    // restriction); an empty set until the boundary has loaded, in which case the
    // header safely falls back to the impersonated user's home org. The backend
    // validates every request regardless. (AUT-10623)
    @computed
    get impersonationAllowedOrgIds(): Set<string> | undefined {
        return this.isImpersonating
            ? new Set(this.impersonationState?.allowedOrganizationIds ?? [])
            : undefined;
    }

    /**
     * Tenant options for the org selector. While impersonating, these are built
     * from the impersonated user's own authorizations plus their home org (NOT
     * from orgStore.organizations, which can be over-broad and/or missing
     * authorized tenants). Normal sessions keep using the loaded org list. (AUT-10307)
     */
    @computed
    get accessibleTenantOptions(): AccessibleTenantOption[] {
        const loadedOrganizations = (this.orgStore?.organizations ?? []).map(
            (org) => ({
                id: org.id ?? "",
                name: org.name ?? "",
                isActive: org.isActive ?? true,
            }),
        );

        const options = this.isImpersonating
            ? buildAccessibleTenantOptions({
                  effectiveOrgId: this.effectiveOrgId,
                  allowedOrgIds: this.impersonationAllowedOrgIds,
                  tenantAuthorizations:
                      this.permStore?.userTenantAuthorizations,
                  loadedOrganizations,
              })
            : loadedOrganizations;

        // Honor the "Display Inactive" preference after membership is resolved.
        return this.orgStore?.showInactiveTenants
            ? options
            : options.filter((option) => option.isActive);
    }

    @computed
    get effectiveUserId(): string {
        return (
            this.impersonationState?.effectiveUser?.userId ??
            this._user?.profile?.uId
        );
    }

    @computed
    get effectiveOrgId(): string {
        return (
            this.impersonationState?.effectiveUser?.organizationId ??
            this._user?.profile?.OrgId
        );
    }

    @computed
    get actualUserId(): string {
        return (
            this.impersonationState?.actualUser?.userId ??
            this._user?.profile?.uId
        );
    }

    @action
    public refreshImpersonationState = async () => {
        if (!this._isAuthenticated) {
            runInAction(() => {
                this.impersonationState = undefined;
            });
            return;
        }

        try {
            const state = await this.impersonationService.getCurrent(
                this.effectiveOrgId,
            );
            runInAction(() => {
                this.impersonationState = state;
            });
        } catch (error) {
            console.error(error);
            runInAction(() => {
                this.impersonationState = undefined;
            });
        }
    };

    public async startImpersonating(targetUserId: string, reason?: string) {
        await this.impersonationService.start(
            targetUserId,
            reason,
            this.effectiveOrgId,
        );
        window.location.assign("/app");
    }

    public async stopImpersonating() {
        await this.impersonationService.stop(this.effectiveOrgId);
        window.location.assign("/app");
    }
}
